/**
 * Shared utility for ReactMarkdown component configurations.
 * Provides consistent rendering of answer lines and other markdown elements
 * across all views (ViewLessonPlan, ViewAssessment, ContentEditor, etc.)
 */

import { defaultUrlTransform } from 'react-markdown';
import rehypeRaw from 'rehype-raw';

/**
 * Custom URL transform that allows data: URLs (used for embedded images)
 * while preserving default sanitization for all other URLs.
 * 
 * React-markdown v10 blocks data: URLs by default for security.
 * We need to allow them because lesson plan images are stored as
 * inline data URLs (base64-encoded).
 *
 * Usage: <ReactMarkdown urlTransform={safeUrlTransform} ...>
 */
export const safeUrlTransform = (url) => {
  if (typeof url === 'string' && url.startsWith('data:image/')) {
    return url;
  }
  return defaultUrlTransform(url);
};

/**
 * Rehype plugins array that enables raw HTML rendering (needed for
 * resized images stored as <img> tags in markdown).
 * Usage: <ReactMarkdown rehypePlugins={rehypePlugins} ...>
 */
export const rehypePlugins = [rehypeRaw];

/**
 * Detects if a text string is an "answer line" - a line meant for student writing.
 * Answer lines are typically rendered as underscores in markdown (e.g., _______________).
 * 
 * @param {string} text - The text to check
 * @returns {boolean} - True if the text is an answer line
 */
export const isAnswerLine = (text) => {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  // Match lines that are only underscores and whitespace, at least 10 characters
  return /^[_\s]{10,}$/.test(trimmed) && trimmed.includes('_');
};

/**
 * Detects if text is a drawing space placeholder from GPT (e.g. [Large blank space for drawing - approximately 10 lines of space]).
 * Standardized: we render these as a visual blank box, never show the raw text.
 *
 * @param {string} text - The text to check
 * @returns {boolean} - True if the text is a drawing space placeholder
 */
export const isDrawingSpace = (text) => {
  if (!text || typeof text !== 'string') return false;
  const str = text.trim();
  return (
    /\[.*blank.*space.*(for )?drawing.*\]/i.test(str) ||
    /\[.*(space|area).*for.*drawing.*\]/i.test(str) ||
    /\[.*drawing.*(space|area|blank).*\]/i.test(str) ||
    /\[.*approximately\s*\d+\s*lines.*(space|for drawing).*\]/i.test(str) ||
    /\[.*\d+\s*lines?\s*(of\s*)?space.*(for\s*)?drawing.*\]/i.test(str)
  );
};

/**
 * Extracts text content from React children.
 * Handles strings, arrays, and nested elements.
 * 
 * @param {*} children - React children
 * @returns {string} - Extracted text
 */
export const extractTextFromChildren = (children) => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map(child => {
      if (typeof child === 'string') return child;
      if (child?.props?.children) return extractTextFromChildren(child.props.children);
      return '';
    }).join('');
  }
  if (children?.props?.children) {
    return extractTextFromChildren(children.props.children);
  }
  return '';
};

/**
 * Creates a paragraph renderer component for ReactMarkdown that handles:
 * - Answer lines (underscore lines for student writing)
 * - Drawing space placeholders
 * - Regular paragraphs
 * 
 * @param {Object} styles - CSS module styles object (must include answerLine, answerLineInner, markdownP, drawingSpace)
 * @returns {Function} - React component for rendering paragraphs
 */
export const createParagraphRenderer = (styles, options = {}) => {
  const { hideDrawingSpace = false } = options;
  return ({ node, children, ...props }) => {
    const text = extractTextFromChildren(children);
    
    // Check for drawing space placeholder
    if (isDrawingSpace(text)) {
      if (hideDrawingSpace) return null;
      return <div className={styles.drawingSpace} aria-label="Space for student drawing" />;
    }
    
    // Check if this is an answer line
    if (isAnswerLine(text)) {
      return (
        <p {...props} className={styles.answerLine}>
          <span className={styles.answerLineInner}></span>
        </p>
      );
    }
    
    // Regular paragraph
    return <p {...props} className={styles.markdownP}>{children}</p>;
  };
};

/**
 * Creates a heading (h2) renderer that generates IDs for anchor linking.
 * 
 * @param {Object} styles - CSS module styles object (must include markdownH2)
 * @returns {Function} - React component for rendering h2 elements
 */
export const createH2Renderer = (styles) => {
  return ({ node, children, ...props }) => {
    const text = extractTextFromChildren(children);
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    return <h2 {...props} id={id} className={styles.markdownH2}>{children}</h2>;
  };
};

/**
 * Returns a complete components configuration object for ReactMarkdown.
 * Use this for consistent markdown rendering across all views.
 * 
 * @param {Object} styles - CSS module styles object
 * @param {Object} options - Optional configuration
 * @param {boolean} options.includeH2Anchors - Whether to generate IDs for h2 (default: true)
 * @param {boolean} options.handleDrawingSpace - Whether to handle drawing space placeholders (default: false)
 * @returns {Object} - Components configuration for ReactMarkdown
 */
export const getMarkdownComponents = (styles, options = {}) => {
  const { includeH2Anchors = true, handleDrawingSpace = false, hideDrawingSpace = false } = options;

  const getImageAlignment = (node, props) => {
    const alignFromProps = props?.['data-align'] || props?.dataAlign || props?.align;
    const alignFromNode = node?.properties?.['data-align'] || node?.properties?.dataAlign || node?.properties?.align;
    const align = (alignFromProps || alignFromNode || 'left').toString().toLowerCase();
    if (align === 'center' || align === 'right') return align;
    return 'left';
  };

  const getImageStyle = (align) => {
    if (align === 'center') {
      return { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 'auto', marginRight: 'auto' };
    }
    if (align === 'right') {
      return { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 'auto', marginRight: 0 };
    }
    return { maxWidth: '100%', height: 'auto', display: 'block', marginLeft: 0, marginRight: 'auto' };
  };
  
  return {
    table: ({ node, ...props }) => <table {...props} className={styles.markdownTable} />,
    thead: ({ node, ...props }) => <thead {...props} className={styles.markdownThead} />,
    tbody: ({ node, ...props }) => <tbody {...props} className={styles.markdownTbody} />,
    tr: ({ node, ...props }) => <tr {...props} className={styles.markdownTr} />,
    th: ({ node, ...props }) => <th {...props} className={styles.markdownTh} />,
    td: ({ node, ...props }) => <td {...props} className={styles.markdownTd} />,
    h2: includeH2Anchors ? createH2Renderer(styles) : ({ node, children, ...props }) => <h2 {...props} className={styles.markdownH2}>{children}</h2>,
    h3: ({ node, children, ...props }) => <h3 {...props} className={styles.markdownH3}>{children}</h3>,
    p: handleDrawingSpace 
      ? createParagraphRenderer(styles, { hideDrawingSpace })
      : ({ node, children, ...props }) => {
          const text = extractTextFromChildren(children);
          if (isAnswerLine(text)) {
            return (
              <p {...props} className={styles.answerLine}>
                <span className={styles.answerLineInner}></span>
              </p>
            );
          }
          return <p {...props} className={styles.markdownP}>{children}</p>;
        },
    ul: ({ node, ...props }) => <ul {...props} className={styles.markdownUl} />,
    ol: ({ node, ...props }) => <ol {...props} className={styles.markdownOl} />,
    li: ({ node, ...props }) => <li {...props} className={styles.markdownLi} />,
    strong: ({ node, ...props }) => <strong {...props} className={styles.markdownBold} />,
    em: ({ node, ...props }) => <em {...props} className={styles.markdownItalic} />,
    img: ({ node, ...props }) => {
      const align = getImageAlignment(node, props);
      return (
        <img
          {...props}
          alt={props.alt || ''}
          style={getImageStyle(align)}
          loading="lazy"
        />
      );
    },
  };
};
